// ⌘K: jump to a project, a page of the current project, an admin page or a docs link.
import { useMemo, useState, type KeyboardEvent } from "react";
import { useConsole } from "../App.tsx";
import { go, projectHref, type Route } from "../router.ts";
import { backendHost } from "../lib/kit.ts";
import { ADMIN_PAGES, ICONS, PRODUCTS } from "./nav.tsx";

interface Item { label: string; sub: string; href: string; icon: string; external?: boolean }

const DOCS: Item[] = [
  { label: "Docs: install and the Firebase swap", sub: "docs", href: "/docs", icon: "docs", external: true },
  { label: "Docs: rules", sub: "docs", href: "/docs#rules", icon: "docs", external: true },
  { label: "Docs: schema and estra push", sub: "docs", href: "/docs#schema", icon: "docs", external: true },
  { label: "Docs: push notifications", sub: "docs", href: "/docs#push", icon: "docs", external: true },
  { label: "Docs: the console", sub: "docs", href: "/docs#console", icon: "docs", external: true },
];

export function Palette({ route, onClose }: { route: Route; onClose: () => void }) {
  const { projects, isAdmin } = useConsole();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const items = useMemo(() => {
    const out: Item[] = [{ label: "All projects", sub: "home", href: "#/", icon: "projects" }, { label: "New project", sub: "create", href: "#/new", icon: "plus" }];
    for (const p of projects ?? []) out.push({ label: p.name, sub: backendHost(p), href: projectHref(p.id), icon: "overview" });
    const cur = projects?.find((p) => p.id === route.id);
    if (cur) {
      for (const prod of PRODUCTS) {
        if (prod.id === "migrate" && cur.source === "fresh") continue;
        out.push({ label: prod.label, sub: cur.name, href: projectHref(cur.id, prod.id), icon: prod.id });
        for (const t of prod.tabs) out.push({ label: `${prod.label} › ${t.label}`, sub: cur.name, href: projectHref(cur.id, prod.id, t.id), icon: prod.id });
      }
    }
    if (isAdmin) for (const a of ADMIN_PAGES) out.push({ label: `Admin › ${a.label}`, sub: "admin", href: `#/admin/${a.id}`, icon: a.icon });
    out.push(...DOCS);
    return out;
  }, [projects, route.id, isAdmin]);
  const shown = items.filter((i) => `${i.label} ${i.sub}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);
  const pick = (i: Item | undefined) => {
    if (!i) return;
    onClose();
    if (i.external) window.open(i.href, "_blank", "noopener");
    else go(i.href);
  };
  const keys = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") setSel((s) => Math.min(shown.length - 1, s + 1));
    else if (e.key === "ArrowUp") setSel((s) => Math.max(0, s - 1));
    else if (e.key === "Enter") pick(shown[sel]);
    else if (e.key === "Escape") onClose();
    else return;
    e.preventDefault();
  };
  return (
    <div className="palette-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Jump to">
        <input autoFocus placeholder="Search projects, pages and docs…" value={q} onChange={(e) => { setQ(e.target.value); setSel(0); }} onKeyDown={keys} aria-label="Search" />
        <ul role="listbox">
          {shown.map((i, k) => (
            <li key={`${i.href}-${k}`}>
              <button className={k === sel ? "on" : ""} role="option" aria-selected={k === sel} onMouseEnter={() => setSel(k)} onClick={() => pick(i)}>
                <span style={{ width: 17, height: 17, color: "var(--muted)", display: "inline-grid" }}>{ICONS[i.icon]}</span>
                {i.label}
                <span className="sub">{i.sub}</span>
              </button>
            </li>
          ))}
          {!shown.length && <li className="muted small" style={{ padding: 12 }}>Nothing matches.</li>}
        </ul>
      </div>
    </div>
  );
}
